import React, { createContext, useContext, useEffect } from "react";
import api from "../api/client";

interface AnalyticsContextProps {
  trackPageView: (path: string, referrer?: string, title?: string) => void;
  trackEvent: (name: string, metadata?: any, value?: number) => void;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
  getVisitorId: () => string;
  getSessionId: () => string | null;
}
interface StartSessionResponse {
  session_id: string;
  visitor_id: string;
  status: string;
}

const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  /* ================= Visitor ================= */
  const getVisitorId = (): string => {
    let id = localStorage.getItem("visitor_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("visitor_id", id);
      localStorage.setItem("first_visit", new Date().toISOString());
    }
    return id;
  };

  /* ================= Session ================= */
  const getSessionId = (): string | null => {
    return localStorage.getItem("session_id");
  };

const startSession = async () => {
  if (localStorage.getItem("session_id")) return;

  const visitorId = getVisitorId();

  const res = await api.post<StartSessionResponse>(
    "/analytics/session/start/",
    {
      visitor_id: visitorId,
      entry_page: window.location.pathname,
      referrer: document.referrer || "",
    }
  );

  const sessionId = res.data.session_id;

  localStorage.setItem("session_id", sessionId);
  localStorage.setItem("session_start", Date.now().toString());
};


  const endSession = async () => {
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) return;

    try {
      await api.post("/analytics/session/end/", {
        session_id: sessionId,
        exit_page: window.location.pathname,
      });
    } catch (e) {
      console.error("Analytics endSession error", e);
    } finally {
      localStorage.removeItem("session_id");
      localStorage.removeItem("session_start");
    }
  };

  /* ================= Page View ================= */
  const trackPageView = (
    path: string,
    referrer: string = document.referrer,
    title: string = document.title
  ) => {
    api.post("/analytics/page-view/", {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      path,
      referrer,
      title,
    }).catch(err => console.error("Analytics PageView Error:", err));
  };

  /* ================= Events ================= */
  const trackEvent = (name: string, metadata: any = {}, value?: number) => {
    api.post("/analytics/event/", {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      name,
      metadata: {
        ...metadata,
        url: window.location.href,
        page_title: document.title,
      },
      value,
    }).catch(err => console.error("Analytics Event Error:", err));
  };

  /* ================= Lifecycle ================= */
  useEffect(() => {
    // بدء session أول ما التطبيق يفتح
    startSession();

    // إنهاء session عند غلق التاب أو الريفرش
    const handleUnload = () => {
      endSession();
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  return (
    <AnalyticsContext.Provider
      value={{
        trackPageView,
        trackEvent,
        startSession,
        endSession,
        getVisitorId,
        getSessionId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/* ================= Hook ================= */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
};
